import { z } from 'zod';
import { MetaSchema } from './create-chat.js';
import { UsagePeriodSchema } from './usage.js';

/**
 * Org activity feed contracts (ADR-0076 §2, D16-04) — the Overview's "Recent
 * activity". Owner/Admin only. `data` is strict (the BFF authors it); the
 * envelope is loose (ADR-0024 §11).
 */

/** What happened. Each kind is written by the handler that performed it. */
export const ActivityKindSchema = z.enum([
  'workstream.created',
  'report.exported',
  'invite.sent',
  'invite.withdrawn',
  'member.role_changed',
  'member.removed',
  'budget.updated',
  'budget.extended',
  'alert.threshold_crossed',
  // AB-2-4: an owner published their agent, or rolled it back to an earlier version.
  'agent.published',
  'agent.rolled_back',
  // AB-2-5: the org library — a share asked for, approved or declined; an agent retired.
  'agent.share_requested',
  'agent.shared',
  'agent.share_declined',
  'agent.retired',
]);
export type ActivityKind = z.infer<typeof ActivityKindSchema>;

export const ActivityEventSchema = z.strictObject({
  /** Stable id (the event's sort-key suffix). */
  id: z.string(),
  kind: ActivityKindSchema,
  /** RFC-3339 UTC. */
  at: z.string(),
  /** Auth0 `sub` of the member who did it, or `system` for an alert (ADR-0078 §3). */
  actorSub: z.string(),
  /** The minimum to render the line: a workstream title, a period, an email, or a budget summary. */
  subject: z.string(),
});
export type ActivityEvent = z.infer<typeof ActivityEventSchema>;

/** `data` of `GET /api/v1/workstreams/activity` — the latest events, newest first. */
export const ActivityDataSchema = z.strictObject({
  events: z.array(ActivityEventSchema),
});
export type ActivityData = z.infer<typeof ActivityDataSchema>;

export const ActivityResponseSchema = z.looseObject({
  data: ActivityDataSchema,
  meta: MetaSchema,
});
export type ActivityResponse = z.infer<typeof ActivityResponseSchema>;

/**
 * Body of `POST /api/v1/workstreams/activity/export` — the SPA reports a CSV
 * export it produced client-side (ADR-0071 §3), so the feed can show it.
 */
export const ActivityExportRequestSchema = z.strictObject({
  period: UsagePeriodSchema,
});
export type ActivityExportRequest = z.infer<typeof ActivityExportRequestSchema>;

export const ActivityExportResponseSchema = z.looseObject({
  data: ActivityEventSchema,
  meta: MetaSchema,
});
